import type { SxProps } from '@mui/system';
import { FieldValues, UseControllerProps } from 'react-hook-form';

import { PictureUploaderFieldUIProps } from '../fields/PictureUploaderField.tsx';
import { OptionalTextFieldProps } from '../fields/OptionalTextField.tsx';
import { FileUploaderFieldProps } from '../fields/FileUploaderField.tsx';
import { DatePickerFieldProps } from '../fields/DatePickerField.tsx';
import { AutocompleteFieldProps } from '../fields/AutocompleteField.tsx';
import { API_FIELDS_CONTACT } from '../configs';

export enum FormFieldType {
  Text = 'text',
  Date = 'date',
  Radio = 'radio',
  Dropdown = 'dropdown',
  Checkbox = 'checkbox',
  Checkboxset = 'checkboxset',
  TagsAutocomplete = 'tags-autocomplete',
  PictureUploader = 'pictureuploader',
  FileUploader = 'fileuploader',
  AddressAutocomplete = 'address-autocomplete',
  SelectionRangeTable = 'selection-range-table',
  Number = 'number',
  Autocomplete = 'autocomplete',
}

export interface BaseFormFieldProps<T extends FieldValues> extends UseControllerProps<T> {
  label?: string;
  readOnly?: boolean;
}

export interface TextFieldFieldProps<T extends FieldValues> extends BaseFormFieldProps<T> {
  type: FormFieldType.Text;
  multiline?: boolean;
  rows?: number;
  textSuffix?: string;
  maxLength?: number;
  helperText?: string;
  placeholder?: string;
  maxWords?: number;
}

export type FieldOption = {
  label?: string;
  value: string;
  textField?: OptionalTextFieldProps['textField'];
};

export type FieldGroupedOption = { group: string; options: Omit<FieldOption, 'textField'>[] };

export interface RadioFormFieldProps<T extends FieldValues> extends BaseFormFieldProps<T> {
  type: FormFieldType.Radio;
  options: FieldOption[];
  direction?: 'horizontal' | 'vertical';
  itemWrapperProps?: { sx?: SxProps; [key: string]: unknown };
}

export interface DopdownFormFieldProps<T extends FieldValues> extends BaseFormFieldProps<T> {
  type: FormFieldType.Dropdown;
  options: Omit<FieldOption, 'textField'>[];
  placeholderValue?: string;
}

export interface CheckboxFormFieldProps<T extends FieldValues> extends BaseFormFieldProps<T> {
  type: FormFieldType.Checkbox;
}

export interface CheckboxsetFormFieldProps<T extends FieldValues> extends BaseFormFieldProps<T> {
  type: FormFieldType.Checkboxset;
  options: FieldOption[];
  direction?: 'horizontal' | 'vertical';
  itemWrapperProps?: { sx?: SxProps; [key: string]: unknown };
}

export interface TagsAutocompleteFormFieldProps<T extends FieldValues> extends BaseFormFieldProps<T> {
  type: FormFieldType.TagsAutocomplete;
  groupedOptions: FieldGroupedOption[];
}

export interface PictureUploaderFormFieldProps<T extends FieldValues> extends BaseFormFieldProps<T> {
  type: FormFieldType.PictureUploader;
  id: PictureUploaderFieldUIProps['id'];
  aspectRatio?: PictureUploaderFieldUIProps['aspectRatio'];
}

export interface FileUploaderFormFieldProps<T extends FieldValues> extends BaseFormFieldProps<T> {
  type: FormFieldType.FileUploader;
  id: FileUploaderFieldProps['id'];
  allowMultiple?: FileUploaderFieldProps['allowMultiple'];
  acceptedMimeTypes?: FileUploaderFieldProps['acceptedMimeTypes'];
}

export interface AddressValue {
  [API_FIELDS_CONTACT.addressLine1]: string;
  [API_FIELDS_CONTACT.addressLine2]: string;
  [API_FIELDS_CONTACT.postalCode]: string;
  [API_FIELDS_CONTACT.city]: string;
  [API_FIELDS_CONTACT.state]: string;
  [API_FIELDS_CONTACT.country]: string;
}

export interface AddressInput {
  name: keyof AddressValue;
  label: string;
  required?: boolean;
}

export interface AutoCompleteAddressFormFieldProps<T extends FieldValues> extends BaseFormFieldProps<T> {
  type: FormFieldType.AddressAutocomplete;
  googleMapsAPIKey: string;
  restrictToUS?: boolean;
  addressInputs?: AddressInput[];
}

export interface SelectionRangeTableOption {
  name: string;
  leftLabel: string;
  rightLabel: string;
}

export interface SelectionRangeTableValue {
  [key: string]: number | '';
}

export interface SelectionRangeTableFormFieldProps<T extends FieldValues> extends BaseFormFieldProps<T> {
  type: FormFieldType.SelectionRangeTable;
  options: SelectionRangeTableOption[];
}
export interface NumberFormFieldProps<T extends FieldValues> extends BaseFormFieldProps<T> {
  type: FormFieldType.Number;
  min?: number;
  max?: number;
  textSuffix?: string;
  helperText?: string;
}

export interface DatePickerFormFieldProps<T extends FieldValues> extends BaseFormFieldProps<T> {
  type: FormFieldType.Date;
  min?: DatePickerFieldProps['min'];
  max?: DatePickerFieldProps['max'];
  maxPeriod?: DatePickerFieldProps['maxPeriod'];
  maxDirection?: DatePickerFieldProps['maxDirection'];
  displayDays?: DatePickerFieldProps['displayDays'];
}

export interface AutocompleteFormFieldProps<T extends FieldValues> extends BaseFormFieldProps<T> {
  type: FormFieldType.Autocomplete;
  groupedOptions: AutocompleteFieldProps['groupedOptions'];
  disabledOptions?: AutocompleteFieldProps['disabledOptions'];
}

export type FormFieldProps<T extends FieldValues> =
  | DatePickerFormFieldProps<T>
  | TextFieldFieldProps<T>
  | RadioFormFieldProps<T>
  | CheckboxFormFieldProps<T>
  | CheckboxsetFormFieldProps<T>
  | DopdownFormFieldProps<T>
  | TagsAutocompleteFormFieldProps<T>
  | PictureUploaderFormFieldProps<T>
  | AutoCompleteAddressFormFieldProps<T>
  | NumberFormFieldProps<T>
  | SelectionRangeTableFormFieldProps<T>
  | FileUploaderFormFieldProps<T>
  | AutocompleteFormFieldProps<T>;
